import { useMemo, useState } from "react";
import {
  nextSort,
  sortByColumn,
  type SortableColumn,
  type SortState,
} from "@bento/shared";

// React port of the legacy initSortableTable's state: which column is sorted
// and in which direction, plus the derived ordered rows. The imperative DOM
// reordering becomes a pure derive-from-state. `resetKey` mirrors the legacy
// behaviour where initSortableTable re-runs (and clears sort) whenever the body
// is re-rendered — e.g. a season change — so a data change returns to the
// original (standings) order.
export interface UseSortableTable<T> {
  /** Rows in the current sort order (original order when unsorted). */
  rows: T[];
  sort: SortState;
  /** Handle a header click for a column: toggle or apply its default. */
  toggle: (col: SortableColumn) => void;
  /** Restore the original order (the "#" rank header). */
  reset: () => void;
}

const UNSORTED: SortState = { key: null, asc: false };

export function useSortableTable<T>(
  original: readonly T[],
  columns: readonly SortableColumn[],
  value: (row: T, key: string) => string | number,
  resetKey?: unknown
): UseSortableTable<T> {
  const [sort, setSort] = useState<SortState>(UNSORTED);

  // Adjust-state-during-render: when the data identity changes (resetKey), drop
  // back to the original order, matching legacy initSortableTable re-runs.
  const [prevKey, setPrevKey] = useState(resetKey);
  if (resetKey !== prevKey) {
    setPrevKey(resetKey);
    setSort(UNSORTED);
  }

  const rows = useMemo(() => {
    if (sort.key === null) return [...original];
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return [...original];
    return sortByColumn(original, col, sort.asc, (r) => value(r, col.key));
  }, [original, columns, sort, value]);

  return {
    rows,
    sort,
    toggle: (col) => setSort((prev) => nextSort(prev, col)),
    reset: () => setSort(UNSORTED),
  };
}
